import { card, el, statusPill } from '../dom';
import {
  APP_ROLE_LABELS,
  BOOKABLE_LABEL,
  CAPABILITY_LABELS,
  CREDENTIAL_LABELS,
  NOT_BOOKABLE_LABEL,
  ROLE_LABELS,
} from '../clinic/labels';

/**
 * SYNTHETIC TEST DATA ONLY
 * Preview chrome so the admin slice is reviewable while the API is paper.
 * Not a clinic, not a person, not copied from live Dubbed.
 */
const SAMPLE_STAFF = [
  {
    id: 'staff-user-000001',
    displayName: 'Staffuser Alpha',
    roleTitle: ROLE_LABELS.owner,
    appRoleTitle: APP_ROLE_LABELS.user,
    credentials: { npiVerified: true, deaVerified: true, stateLicenseVerified: true },
  },
  {
    id: 'staff-user-000002',
    displayName: 'Staffuser Charlie',
    roleTitle: ROLE_LABELS.staff,
    appRoleTitle: APP_ROLE_LABELS.user,
    credentials: { npiVerified: true, deaVerified: false, stateLicenseVerified: true },
  },
  {
    id: 'staff-user-000005',
    displayName: 'Staffuser Manager',
    roleTitle: ROLE_LABELS.manager,
    appRoleTitle: APP_ROLE_LABELS.user,
    credentials: { npiVerified: true, deaVerified: true, stateLicenseVerified: false },
  },
] as const;

const SAMPLE_LOCATIONS = [
  { id: 'location-000001', name: 'Location Alpha One' },
] as const;

function bookable(credentials: (typeof SAMPLE_STAFF)[number]['credentials']): boolean {
  return credentials.npiVerified && credentials.deaVerified && credentials.stateLicenseVerified;
}

function check(label: string, on: boolean): HTMLElement {
  return el(
    'li',
    { class: on ? 'check check--on' : 'check' },
    el('span', { class: 'check__mark', 'aria-hidden': 'true' }, on ? '✓' : '·'),
    el('span', {}, label),
  );
}

function staffRow(row: (typeof SAMPLE_STAFF)[number]): HTMLElement {
  const ready = bookable(row.credentials);
  return el(
    'article',
    { class: 'profile' },
    el(
      'header',
      { class: 'profile__head' },
      el('h3', {}, row.displayName),
      statusPill(ready ? 'live' : 'pending', ready ? BOOKABLE_LABEL : NOT_BOOKABLE_LABEL),
    ),
    el(
      'p',
      { class: 'profile__meta' },
      `${row.roleTitle} · ${row.appRoleTitle} · ${row.id}`,
    ),
    el(
      'ul',
      { class: 'checks', 'aria-label': 'Credentials' },
      check(CREDENTIAL_LABELS.npiVerified, row.credentials.npiVerified),
      check(CREDENTIAL_LABELS.deaVerified, row.credentials.deaVerified),
      check(CREDENTIAL_LABELS.stateLicenseVerified, row.credentials.stateLicenseVerified),
    ),
  );
}

export function renderAdmin(mount: HTMLElement): void {
  const heading = el(
    'section',
    { class: 'section' },
    el('h2', {}, 'Clinic OS'),
    el(
      'p',
      { class: 'lede' },
      'Staff list, first-class locations, and thin profile permissions. This slice extends the staff door. It does not copy live Dubbed, and it does not touch patients, chart, Rx, or POS.',
    ),
  );

  const rules = card({
    title: 'Who may do what',
    tone: 'held',
    toneLabel: 'Tenancy',
    body: 'Platform admin is the Cognito group platform-admin plus app_role admin. A clinic Owner is not a platform admin. Only a platform admin or an Owner may create locations or invite owners.',
    facts: [
      [CAPABILITY_LABELS.create_location, 'Platform admin or Owner'],
      [CAPABILITY_LABELS.invite_owner, 'Platform admin or Owner'],
      [CAPABILITY_LABELS.view_staff, 'Any member of that clinic'],
      [CAPABILITY_LABELS.view_locations, 'Any member of that clinic'],
    ],
  });

  const roles = card({
    title: 'Role titles',
    tone: 'live',
    toneLabel: 'Live labels',
    body: 'Membership roles and the platform app_role. Titles match the product copy.',
    facts: [
      ['Owner', ROLE_LABELS.owner],
      ['Manager', ROLE_LABELS.manager],
      ['Staff', ROLE_LABELS.staff],
      ['Medical Director', ROLE_LABELS.medical_director],
      ['Platform admin', APP_ROLE_LABELS.admin],
      ['User', APP_ROLE_LABELS.user],
    ],
  });

  const staffSection = el(
    'section',
    { class: 'section' },
    el('h3', {}, 'Staff'),
    el(
      'p',
      { class: 'note' },
      'Synthetic preview rows. Unchecked NPI, DEA, or State license means Not bookable. No staff_toggles matrix.',
    ),
    el('div', { class: 'profiles' }, ...SAMPLE_STAFF.map(staffRow)),
  );

  const locations = el(
    'section',
    { class: 'section' },
    el('h3', {}, 'Locations'),
    el(
      'p',
      { class: 'note' },
      'First-class locations belong to a clinic. This is not a parent_clinic_id hack.',
    ),
    el(
      'ul',
      { class: 'lanes' },
      ...SAMPLE_LOCATIONS.map((location) =>
        el(
          'li',
          { class: 'lane' },
          el(
            'div',
            { class: 'lane__head' },
            el('span', { class: 'lane__name' }, location.name),
            el('code', { class: 'lane__repo' }, location.id),
          ),
          el('p', { class: 'lane__owns' }, 'Clinic Alpha · first-class location'),
        ),
      ),
    ),
  );

  mount.append(heading, el('div', { class: 'grid' }, rules, roles), staffSection, locations);
}
